import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../../components/FormValidation";
import useFormValidation from "../../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../../utils/helpers/formValidation";
import { readImageFile } from "../../../../utils/helpers/imageUpload";
import AccountAvatar from "../AccountAvatar";
import AkasSelect from "../../../../components/AkasSelect";

const FormIcon = ({ name, size = 19 }) => {
  const icons = {
    user: <><path d="M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92Z" /></>,
    at: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" /></>,
    lock: <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12" /><circle cx="12" cy="12" r="3" /></>,
    eyeOff: <><path d="m3 3 18 18" /><path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" /><path d="M9.9 4.2A9.8 9.8 0 0 1 12 4c6.5 0 10 8 10 8a17.7 17.7 0 0 1-2.1 3.2M6.6 6.6A16.8 16.8 0 0 0 2 12s3.5 8 10 8a9.8 9.8 0 0 0 5.4-1.6" /></>,
    upload: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M5 20h14" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></>,
    store: <><path d="M3 21h18" /><path d="M5 21V8l7-4 7 4v13" /><path d="M9 21v-6h6v6" /></>,
    save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" /><path d="M17 21v-8H7v8M7 3v5h8" /></>,
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icons[name] || icons.user}
    </svg>
  );
};

const InputField = ({ label, icon, name, error, required = false, children }) => (
  <div className="account-form-field">
    <label className="form-label" htmlFor={`account-${name}`}>{label}<RequiredMark optional={!required} /></label>
    <div className="account-input-wrap">
      <span><FormIcon name={icon} /></span>
      {children}
    </div>
    <FieldFeedback formId="account" name={name} error={error} />
  </div>
);

const FormAccount = ({
  roles,
  outlets,
  isShowModal,
  isEdit,
  closeModal,
  formState,
  setFormState,
  handleAdd,
  handleUpdate,
  isLoading = false,
  canConfigureSellerStock = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const nameInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const validationSchema = useMemo(() => ({
    name: [rule.required("Nama lengkap wajib diisi."), rule.maxLength(255)],
    email: [rule.required("Email wajib diisi."), rule.email(), rule.maxLength(255)],
    username: [rule.required("Username wajib diisi."), rule.maxLength(255)],
    phone: rule.phone(),
    password: isEdit
      ? rule.minLength(6, "Password minimal 6 karakter.")
      : [rule.required("Password wajib diisi."), rule.minLength(6, "Password minimal 6 karakter.")],
    role_id: rule.required("Role wajib dipilih."),
    outlet_ids: rule.custom((value) => Array.isArray(value) && value.length ? "" : "Pilih minimal satu outlet."),
  }), [isEdit]);
  const validation = useFormValidation({
    values: formState,
    schema: validationSchema,
    formId: "account",
    focusRoot: formRef,
  });
  const { resetValidation } = validation;

  useEffect(() => {
    if (!isShowModal) return;
    resetValidation();
    setShowPassword(false);
    window.setTimeout(() => nameInputRef.current?.focus(), 80);
  }, [isShowModal, resetValidation]);

  const updateField = (field, value) => setFormState((current) => ({ ...current, [field]: value }));

  const handleImage = async (file) => {
    if (!file) return;
    try {
      const base64 = await readImageFile(file, { label: "Foto akun", preset: "avatar" });
      setFormState((current) => ({
        ...current,
        image: base64,
        image_path: base64,
        file_name: file.name,
      }));
    } catch (error) {
      toast.error(error?.message || "Foto akun tidak dapat diproses.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validation.validateAll() || isLoading) return;
    await (isEdit ? handleUpdate() : handleAdd());
  };

  if (!isShowModal) return null;

  return (
    <div className="account-modal-backdrop" role="presentation">
      <div className="account-modal" role="dialog" aria-modal="true" aria-labelledby="account-form-title">
        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          <header className="account-modal-header">
            <div>
              <small>{isEdit ? "PERBARUI PENGGUNA" : "PENGGUNA BARU"}</small>
              <h3 id="account-form-title">{isEdit ? "Edit akun tim" : "Tambah akun tim"}</h3>
              <p>{isEdit ? "Sesuaikan identitas dan cakupan akses pengguna." : "Siapkan identitas login dan akses kerja pengguna."}</p>
            </div>
            <button type="button" className="btn-close" aria-label="Tutup" disabled={isLoading} onClick={closeModal} />
          </header>

          <main className="account-modal-body">
            <FormValidationNotice errors={validation.errors} showSummary />
            <div className="account-form-layout">
              <aside className="account-photo-panel">
                <div className="account-photo-preview">
                  <AccountAvatar
                    name={formState?.name || formState?.username || "Akun baru"}
                    image={formState?.image_path || formState?.image}
                    size="xl"
                  />
                  <div>
                    <strong>{formState?.name || "Foto profil"}</strong>
                    <span>{formState?.file_name || "JPG, PNG, atau WEBP"}</span>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  className="d-none"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={(event) => handleImage(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className={`account-upload-zone ${isDragging ? "is-dragging" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    handleImage(event.dataTransfer.files?.[0]);
                  }}>
                  <span><FormIcon name="upload" /></span>
                  <strong>{formState?.image_path || formState?.image ? "Ganti foto" : "Pilih foto"}</strong>
                  <small>Maksimal 5 MB · dioptimalkan otomatis</small>
                </button>
                <div className="account-photo-note">
                  Foto membantu owner mengenali aktivitas pengguna pada audit dan transaksi.
                </div>
              </aside>

              <div className="account-form-content">
                <section className="account-form-section">
                  <div className="account-section-heading">
                    <span><FormIcon name="user" /></span>
                    <div><h4>Identitas pengguna</h4><p>Informasi yang digunakan untuk login dan audit aktivitas.</p></div>
                  </div>
                  <div className="account-field-grid">
                    <InputField label="Nama lengkap" icon="user" name="name" error={validation.errors.name} required>
                      <input id="account-name" ref={nameInputRef} {...validation.fieldProps("name")} className={validation.inputClass("name")} type="text" maxLength="255" value={formState?.name || ""} placeholder="Contoh: Raka Pratama" onChange={(event) => updateField("name", event.target.value)} />
                    </InputField>
                    <InputField label="Username" icon="at" name="username" error={validation.errors.username} required>
                      <input id="account-username" {...validation.fieldProps("username")} className={validation.inputClass("username")} type="text" maxLength="255" autoCapitalize="none" value={formState?.username || ""} placeholder="raka.kasir" onChange={(event) => updateField("username", event.target.value)} />
                    </InputField>
                    <InputField label="Email" icon="mail" name="email" error={validation.errors.email} required>
                      <input id="account-email" {...validation.fieldProps("email")} className={validation.inputClass("email")} type="email" maxLength="255" autoCapitalize="none" value={formState?.email || ""} placeholder="raka@tokomu.id" onChange={(event) => updateField("email", event.target.value)} />
                    </InputField>
                    <InputField label="Nomor HP" icon="phone" name="phone" error={validation.errors.phone}>
                      <input id="account-phone" {...validation.fieldProps("phone")} className={validation.inputClass("phone")} type="tel" inputMode="numeric" maxLength="15" value={formState?.phone || ""} placeholder="081234567890" onChange={(event) => updateField("phone", event.target.value.replace(/\D/g, "").slice(0, 15))} />
                    </InputField>
                  </div>
                </section>

                <section className="account-form-section">
                  <div className="account-section-heading">
                    <span><FormIcon name="shield" /></span>
                    <div><h4>Keamanan & peran</h4><p>Tentukan kewenangan pengguna di dalam AKAS.</p></div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="account-stock-source">Sumber stok POS</label>
                    <AkasSelect id="account-stock-source" className="form-select" value={formState?.pos_stock_source || "outlet"} disabled={!canConfigureSellerStock || isLoading} onChange={(event) => updateField("pos_stock_source", event.target.value)}>
                      <option value="outlet">Stok outlet — yang belum dibagikan</option>
                      <option value="seller">Pegangan sendiri — diterima dari penyerahan</option>
                    </AkasSelect>
                    <p className="text-secondary small mt-2 mb-0">Pegangan sendiri hanya menjual produk siap jual yang sudah diterima. Atur izin penyerahan dan pengembalian melalui Role.</p>
                  </div>
                  <div className="account-field-grid">
                    <InputField label="Role" icon="shield" name="role_id" error={validation.errors.role_id} required>
                      <AkasSelect id="account-role" {...validation.fieldProps("role_id")} className={validation.inputClass("role_id", "form-select")} value={formState?.role_id || ""} onChange={(event) => {
                        const role = (roles || []).find((item) => Number(item.id) === Number(event.target.value));
                        setFormState((current) => ({ ...current, role_id: event.target.value, role_name: role?.name || "" }));
                      }}>
                        <option value="">Pilih role</option>
                        {(roles || []).map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
                      </AkasSelect>
                    </InputField>
                    <InputField label={isEdit ? "Password baru" : "Password"} icon="lock" name="password" error={validation.errors.password} required={!isEdit}>
                      <input id="account-password" {...validation.fieldProps("password")} className={`${validation.inputClass("password")} account-password-input`} type={showPassword ? "text" : "password"} minLength="6" autoComplete="new-password" value={formState?.password || ""} placeholder={isEdit ? "Kosongkan jika tidak diubah" : "Minimal 6 karakter"} onChange={(event) => updateField("password", event.target.value)} />
                      <button type="button" className="account-password-toggle" aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"} onClick={() => setShowPassword((visible) => !visible)}>
                        <FormIcon name={showPassword ? "eyeOff" : "eye"} size={18} />
                      </button>
                    </InputField>
                  </div>
                </section>

                <section className="account-form-section">
                  <div className="account-section-heading">
                    <span><FormIcon name="store" /></span>
                    <div><h4>Akses outlet <RequiredMark /></h4><p>Pilih lokasi tempat akun ini dapat bekerja.</p></div>
                  </div>
                  <div className={`account-outlet-options ${validation.errors.outlet_ids ? "is-invalid" : ""}`} data-validation-field="outlet_ids" tabIndex="-1">
                    {(outlets || []).length ? (outlets || []).map((outlet) => {
                      const outletId = Number(outlet.id || outlet.outlet_id);
                      const outletName = outlet.name || outlet.outlet_name || "Outlet";
                      const outletCode = outlet.code || outlet.outlet_code || "";
                      const selected = (formState?.outlet_ids || []).map(Number).includes(outletId);
                      return (
                        <label className={`account-outlet-option ${selected ? "is-selected" : ""}`} key={outletId}>
                          <input type="checkbox" className="form-check-input" checked={selected} onChange={(event) => {
                            setFormState((current) => ({
                              ...current,
                              outlet_ids: event.target.checked
                                ? [...new Set([...(current.outlet_ids || []).map(Number), outletId])]
                                : (current.outlet_ids || []).map(Number).filter((id) => id !== outletId),
                            }));
                            if (validation.hasSubmitted) window.setTimeout(() => validation.validateField("outlet_ids", { force: true }), 0);
                          }} />
                          <span><strong>{outletName}</strong><small>{outletCode || "Lokasi operasional"}{outlet.is_default ? " · outlet utama" : ""}</small></span>
                          <i><FormIcon name="store" size={17} /></i>
                        </label>
                      );
                    }) : <div className="account-outlet-empty">Belum ada outlet yang dapat ditugaskan.</div>}
                  </div>
                  <FieldFeedback formId="account" name="outlet_ids" error={validation.errors.outlet_ids} />
                </section>
              </div>
            </div>
          </main>

          <footer className="account-modal-footer">
            <button type="button" className="btn" disabled={isLoading} onClick={closeModal}>Batal</button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <FormIcon name="save" />}
              {isLoading ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Tambah Akun"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default FormAccount;
